import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  Flex,
  Grid,
  Heading,
  IconButton,
  Select,
  Text,
  TextField,
  Tooltip
} from '@radix-ui/themes'
import { PlusIcon, TrashIcon } from '@radix-ui/react-icons'
import { Fragment, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { Ops } from '@willie/schemas'
import { useTRPC } from '~/utils/trpc'
import { plural } from '~/utils/plural'
import { FieldLabel } from '~/components/FieldLabel'
import { MessageCallout } from '~/components/MessageCallout'
import {
  duplicateSiteNames,
  emptySite,
  emptyStation,
  existingSite,
  isSiteValid,
  isStationValid,
  sitesMissingContact,
  toBatchInput,
  type BatchDraft
} from '~/features/installations/batchDraft'
import { BatchPhotosStep } from '~/features/installations/components/BatchPhotosStep'
import {
  SitesCsvImport,
  StationsCsvImport,
  type AppliedCsv
} from '~/features/installations/components/BatchCsvImport'
import { SiteStationsSection, type StationEntry } from '~/features/installations/components/SiteStationsSection'
import { ContactPicker } from '~/features/contacts/components/ContactPicker'

const NEW_ORG = '__new_org__'

// Past this, a card per station runs to pages of scrolling, so each station takes one line instead.
const COMPACT_STATIONS_THRESHOLD = 12

const OrgSelect = ({
  value,
  onChange,
  orgs,
  disabled
}: {
  value: string | null
  onChange: (orgId: string) => void
  orgs: { id: string; name: string }[]
  disabled: boolean
}) => (
  <Select.Root value={value ?? undefined} disabled={disabled} onValueChange={onChange}>
    <Select.Trigger className="w-full" placeholder="Select an organization…" />
    <Select.Content>
      <Select.Item value={NEW_ORG}>+ Create new organization…</Select.Item>
      {orgs.length > 0 ? <Select.Separator /> : null}
      {orgs.map((o) => (
        <Select.Item key={o.id} value={o.id}>
          {o.name}
        </Select.Item>
      ))}
    </Select.Content>
  </Select.Root>
)

const WIZARD_STEPS = ['org', 'sites', 'stations', 'photos'] as const
type WizardStep = (typeof WIZARD_STEPS)[number]

type OrgMode = 'existing' | 'new'

const STEP_LABEL: Record<WizardStep, string> = {
  org: 'Organization',
  sites: 'Sites',
  stations: 'Stations',
  photos: 'Photos'
}

const EMPTY_DRAFT: BatchDraft = { orgId: '', kickoffDate: '', plannedInstallationDate: '', sites: [], stations: [] }

const StepIndicator = ({ current }: { current: WizardStep }) => {
  const currentIndex = WIZARD_STEPS.indexOf(current)
  return (
    <Flex gap="2" align="center">
      {WIZARD_STEPS.map((step, index) => (
        <Fragment key={step}>
          {index > 0 ? <Box className="h-px w-6 bg-[var(--gray-6)]" /> : null}
          <Badge color={index <= currentIndex ? 'blue' : 'gray'} variant={index === currentIndex ? 'solid' : 'soft'}>
            {index + 1}. {STEP_LABEL[step]}
          </Badge>
        </Fragment>
      ))}
    </Flex>
  )
}

const BatchBuilder = () => {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<WizardStep>('org')
  const [orgMode, setOrgMode] = useState<OrgMode>('existing')
  const [newOrgName, setNewOrgName] = useState('')
  const [sitesCsv, setSitesCsv] = useState<AppliedCsv | null>(null)
  const [stationsCsv, setStationsCsv] = useState<AppliedCsv | null>(null)
  const [filledFromCsv, setFilledFromCsv] = useState(false)

  const orgsQuery = useQuery(trpc.ops.orgs.list.queryOptions())

  const { control, register, setValue, watch, reset } = useForm<BatchDraft>({ defaultValues: EMPTY_DRAFT })
  const {
    fields: siteFields,
    append: appendSite,
    remove: removeSite,
    replace: replaceSites
  } = useFieldArray({ control, name: 'sites' })
  const {
    fields: stationFields,
    append: appendStation,
    remove: removeStation,
    replace: replaceStations
  } = useFieldArray({ control, name: 'stations' })

  const orgId = watch('orgId')
  const kickoffDate = watch('kickoffDate')
  const plannedInstallationDate = watch('plannedInstallationDate')
  const sites = watch('sites')
  const stations = watch('stations')

  const sitesQuery = useQuery({
    ...trpc.ops.sites.list.queryOptions({ orgId }),
    enabled: !!orgId
  })
  const orgSites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data])

  const [createdIds, setCreatedIds] = useState<number[]>([])

  const batchCreateMutation = useMutation(
    trpc.ops.stationInstallations.batchCreate.mutationOptions({
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.listSiteStations.queryKey() })
        setCreatedIds(result.installationIds)
        setStep('photos')
      }
    })
  )

  const disabled = batchCreateMutation.isPending

  const handleOrgChange = (next: string) => {
    reset({ ...EMPTY_DRAFT, orgId: next })
    setSitesCsv(null)
    setStationsCsv(null)
    setFilledFromCsv(false)
  }

  const createOrgMutation = useMutation(
    trpc.ops.orgs.create.mutationOptions({
      onSuccess: (org) => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.orgs.list.queryKey() })
        // Now that it exists it is an existing org, so going Back cannot offer to create it again.
        setOrgMode('existing')
        setNewOrgName('')
        handleOrgChange(org.id)
        setStep('sites')
      }
    })
  )

  const handleOrgSelect = (value: string) => {
    const nextMode: OrgMode = value === NEW_ORG ? 'new' : 'existing'
    setOrgMode(nextMode)
    // Clearing the pick keeps Next from advancing on an org the operator is no longer looking at.
    handleOrgChange(nextMode === 'new' ? '' : value)
  }

  const siteIndexById = new Map(sites.map((site, index) => [site.siteId, index]))
  const siteKeysWithStations = new Set(stations.map((station) => station.siteKey))
  // Appends in place: this runs on every keystroke, and copying a site's list per station is quadratic.
  const stationEntriesBySite = stations.reduce<Map<string, StationEntry[]>>((acc, station, index) => {
    const entry = { station, index, fieldId: stationFields[index]?.id ?? String(index) }
    const entries = acc.get(station.siteKey)
    if (entries) entries.push(entry)
    else acc.set(station.siteKey, [entry])
    return acc
  }, new Map())
  const adoptedBy = new Map(
    stations.flatMap((station, index) =>
      station.mode === 'adopt' && station.waterStationId ? [[station.waterStationId, index] as const] : []
    )
  )

  const handleToggleSite = (siteId: string, name: string, checked: boolean) => {
    if (checked) {
      appendSite(existingSite(siteId, name))
      return
    }
    const index = siteIndexById.get(siteId)
    if (index !== undefined) handleRemoveSite(index)
  }

  const handleRemoveSite = (index: number) => {
    const key = sites[index]?.key
    removeStation(stations.flatMap((station, stationIndex) => (station.siteKey === key ? [stationIndex] : [])))
    removeSite(index)
  }

  // Only the sites being created are edited by hand; ticked ones are named already.
  const newSiteEntries = sites
    .map((site, index) => ({ site, index, fieldId: siteFields[index]?.id ?? String(index) }))
    .filter((entry) => entry.site.siteId === null)

  const validSites = sites.filter(isSiteValid)
  const duplicates = duplicateSiteNames(sites)
  const missingContact = sitesMissingContact(sites)
  const canLeaveOrg =
    orgMode === 'new' ? newOrgName.trim().length > 0 && !createOrgMutation.isPending : !!orgId
  const tooManySites = sites.length > Ops.StationInstallations.BATCH_MAX_SITES
  const canLeaveSites =
    validSites.length === sites.length && sites.length > 0 && duplicates.length === 0 && !tooManySites
  const everySiteHasAStation = sites.every((site) => siteKeysWithStations.has(site.key))
  const canSubmit =
    canLeaveOrg &&
    canLeaveSites &&
    stations.length > 0 &&
    stations.length <= Ops.StationInstallations.BATCH_MAX_INSTALLATIONS &&
    everySiteHasAStation &&
    stations.every(isStationValid) &&
    !disabled
  const compactStations = stations.length > COMPACT_STATIONS_THRESHOLD

  const handleSubmit = () => {
    if (!canSubmit) return
    batchCreateMutation.mutate(
      toBatchInput(
        { orgId, kickoffDate, plannedInstallationDate, sites, stations },
        filledFromCsv ? 'csv' : 'batchBuilder'
      )
    )
  }

  return (
    <Flex direction="column" gap="4">
      <StepIndicator current={step} />

      {step === 'org' ? (
        <Card>
          <Flex direction="column" gap="3">
            <Heading size="3">Organization</Heading>
            <Text size="2" color="gray">
              Every installation in this batch is created under this organization.
            </Text>
            <Box maxWidth="360px">
              <FieldLabel>Organization</FieldLabel>
              <OrgSelect
                value={orgMode === 'new' ? NEW_ORG : orgId || null}
                onChange={handleOrgSelect}
                orgs={orgsQuery.data ?? []}
                disabled={disabled || createOrgMutation.isPending}
              />
            </Box>
            {orgMode === 'new' ? (
              <Box maxWidth="360px">
                <FieldLabel>New organization name</FieldLabel>
                <TextField.Root
                  placeholder="e.g. Acme Corp"
                  value={newOrgName}
                  disabled={disabled || createOrgMutation.isPending}
                  onChange={(e) => setNewOrgName(e.target.value)}
                />
              </Box>
            ) : null}
            {createOrgMutation.error ? (
              <MessageCallout color="red" message={createOrgMutation.error.message} />
            ) : null}

            <Box className="border-t border-t-[color:var(--gray-6)] pt-3">
              <Text size="2" color="gray" as="p" mb="2">
                Dates apply to every installation in this batch.
              </Text>
              <Flex gap="3" wrap="wrap">
                <Box className="grow basis-48">
                  <FieldLabel>Kickoff date</FieldLabel>
                  <TextField.Root type="date" disabled={disabled} {...register('kickoffDate')} />
                </Box>
                <Box className="grow basis-48">
                  <FieldLabel>Expected installation date</FieldLabel>
                  <TextField.Root type="date" disabled={disabled} {...register('plannedInstallationDate')} />
                </Box>
              </Flex>
            </Box>
          </Flex>
        </Card>
      ) : null}

      {step === 'sites' ? (
        <Flex direction="column" gap="3">
          <SitesCsvImport
            orgId={orgId}
            applied={sitesCsv}
            disabled={disabled}
            onApply={(result) => {
              replaceSites(result.sites)
              replaceStations(result.stations)
              setSitesCsv(result.sitesCsv)
              setStationsCsv(result.stationsCsv)
              setFilledFromCsv(true)
            }}
            onDismiss={() => setSitesCsv(null)}
          />

          <Card>
            <Flex direction="column" gap="3">
              <Heading size="3">Sites</Heading>
              <Text size="2" color="gray">
                Tick the sites this batch covers. Add one only if it does not exist yet.
              </Text>

              {sitesQuery.isLoading ? (
                <Text size="2" color="gray">
                  Loading…
                </Text>
              ) : orgSites.length === 0 ? (
                <Text size="2" color="gray">
                  This organization has no sites yet — add one below.
                </Text>
              ) : (
                <Flex direction="column" gap="2">
                  {orgSites.map((orgSite) => {
                    const index = siteIndexById.get(orgSite.id) ?? -1
                    return (
                      <Box key={orgSite.id}>
                        <label className="flex items-center gap-2 py-1">
                          <Checkbox
                            checked={index !== -1}
                            disabled={disabled}
                            onCheckedChange={(checked) =>
                              handleToggleSite(orgSite.id, orgSite.name, checked === true)
                            }
                          />
                          <Text size="2">{orgSite.name}</Text>
                        </label>
                        {index !== -1 ? (
                          <Grid columns="2" gap="4" ml="5" mt="1" maxWidth="720px">
                            <ContactPicker
                              orgId={orgId}
                              value={sites[index]?.siteContactId ?? null}
                              disabled={disabled}
                              onChange={(contactId) => setValue(`sites.${index}.siteContactId`, contactId)}
                            />
                            <ContactPicker
                              orgId={orgId}
                              value={sites[index]?.installationContactId ?? null}
                              disabled={disabled}
                              onChange={(contactId) => setValue(`sites.${index}.installationContactId`, contactId)}
                              label="Installation contact"
                            />
                          </Grid>
                        ) : null}
                      </Box>
                    )
                  })}
                </Flex>
              )}
            </Flex>
          </Card>

          {newSiteEntries.length > 0 ? (
            <Card>
              <Flex direction="column" gap="3">
                <Heading size="3">New sites</Heading>
                {newSiteEntries.map(({ site, index, fieldId }) => (
                  <Box key={fieldId} className="border-t border-t-[color:var(--gray-6)] pt-3 first:border-0 first:pt-0">
                    <Flex gap="2" align="end">
                      <Box className="grow">
                        <FieldLabel>Name</FieldLabel>
                        <TextField.Root
                          placeholder="e.g. Acme — North plant"
                          disabled={disabled}
                          {...register(`sites.${index}.siteName`)}
                        />
                      </Box>
                      <Tooltip content="Remove site">
                        <IconButton
                          variant="soft"
                          color="gray"
                          size="1"
                          disabled={disabled}
                          onClick={() => handleRemoveSite(index)}
                        >
                          <TrashIcon />
                        </IconButton>
                      </Tooltip>
                    </Flex>
                    <Grid columns="2" gap="4" mt="2" maxWidth="720px">
                      <ContactPicker
                        orgId={orgId}
                        value={site.siteContactId}
                        disabled={disabled}
                        onChange={(contactId) => setValue(`sites.${index}.siteContactId`, contactId)}
                      />
                      <ContactPicker
                        orgId={orgId}
                        value={site.installationContactId}
                        disabled={disabled}
                        onChange={(contactId) => setValue(`sites.${index}.installationContactId`, contactId)}
                        label="Installation contact"
                      />
                    </Grid>
                  </Box>
                ))}
              </Flex>
            </Card>
          ) : null}

          {duplicates.length > 0 ? (
            <MessageCallout color="red" message={`Named more than once: ${duplicates.join(', ')}`} />
          ) : null}

          {tooManySites ? (
            <MessageCallout
              color="red"
              message={`A batch holds at most ${Ops.StationInstallations.BATCH_MAX_SITES} sites, this one has ${sites.length} — split it into several batches`}
            />
          ) : null}

          {missingContact.length > 0 ? (
            <MessageCallout
              color="red"
              message={`A new site needs a contact — nobody to welcome on: ${missingContact.join(', ')}`}
            />
          ) : null}

          <Box>
            <Button
              variant="soft"
              color="gray"
              disabled={disabled || siteFields.length >= Ops.StationInstallations.BATCH_MAX_SITES}
              onClick={() => appendSite(emptySite())}
            >
              <PlusIcon />
              Add another site
            </Button>
          </Box>
        </Flex>
      ) : null}

      {step === 'stations' ? (
        <Flex direction="column" gap="4">
          <StationsCsvImport
            sites={sites}
            applied={stationsCsv}
            disabled={disabled}
            onApply={(result) => {
              replaceStations(result.stations)
              setStationsCsv(result.stationsCsv)
              setFilledFromCsv(true)
            }}
            onDismiss={() => setStationsCsv(null)}
          />
          {sites.map((site) => (
            <SiteStationsSection
              key={site.key}
              site={site}
              entries={stationEntriesBySite.get(site.key) ?? []}
              adoptedBy={adoptedBy}
              compact={compactStations}
              canAdd={stations.length < Ops.StationInstallations.BATCH_MAX_INSTALLATIONS}
              disabled={disabled}
              register={register}
              setValue={setValue}
              onAdd={() => appendStation(emptyStation(site.key))}
              onRemove={removeStation}
            />
          ))}
        </Flex>
      ) : null}

      {step === 'photos' ? (
        <Flex direction="column" gap="3">
          <Callout.Root color="green" size="1">
            <Callout.Text>
              {plural(createdIds.length, 'installation')} created.
            </Callout.Text>
          </Callout.Root>
          <BatchPhotosStep installationIds={createdIds} />
        </Flex>
      ) : null}

      {batchCreateMutation.error ? <MessageCallout color="red" message={batchCreateMutation.error.message} /> : null}

      <Flex justify="between" align="center" gap="2">
        <Text size="2" color="gray">
          {step === 'stations'
            ? `Creates ${plural(stations.length, 'installation')} across ${plural(sites.length, 'site')}`
            : ''}
        </Text>
        <Flex gap="2">
          {step === 'photos' ? (
            <Button
              variant="solid"
              onClick={() => navigate({ to: '/installations', search: { orgId: orgId || undefined } })}
            >
              Done
            </Button>
          ) : (
            <>
              <Button asChild variant="soft" color="gray">
                <Link to="/installations">Cancel</Link>
              </Button>
              {step !== 'org' ? (
                <Button
                  variant="soft"
                  color="gray"
                  disabled={disabled}
                  onClick={() => setStep(step === 'stations' ? 'sites' : 'org')}
                >
                  Back
                </Button>
              ) : null}
              {step === 'stations' ? (
                <Button variant="solid" disabled={!canSubmit} loading={disabled} onClick={handleSubmit}>
                  Create {plural(stations.length, 'installation')}
                </Button>
              ) : (
                <Button
                  variant="solid"
                  disabled={step === 'org' ? !canLeaveOrg : !canLeaveSites}
                  loading={createOrgMutation.isPending}
                  onClick={() => {
                    if (step === 'org') {
                      // Creating advances on success, so the write and the step are one action.
                      if (orgMode === 'new') createOrgMutation.mutate({ name: newOrgName.trim() })
                      else setStep('sites')
                      return
                    }
                    // Every site needs at least one station, so seed the ones that have none.
                    const unseeded = sites.filter((site) => !siteKeysWithStations.has(site.key))
                    if (unseeded.length > 0) appendStation(unseeded.map((site) => emptyStation(site.key)))
                    setStep('stations')
                  }}
                >
                  Next
                </Button>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

const NewInstallationPage = () => (
  <Flex direction="column" gap="4">
    <Heading size="6">New installations</Heading>
    <BatchBuilder />
  </Flex>
)

export const Route = createFileRoute('/installations/new')({
  component: NewInstallationPage
})
